<template>
  <div style="background:#fff; padding:20px;" v-permission="'app:withdraw:list'">
    <!-- 顶部搜索区: 风格对齐 financial-flow,a-form layout="inline" -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="状态">
          <a-select
            v-model="query.status"
            placeholder="全部"
            allow-clear
            style="width: 140px"
          >
            <a-select-option :value="0">待审核</a-select-option>
            <a-select-option :value="1">待确认收款</a-select-option>
            <a-select-option :value="2">打款成功</a-select-option>
            <a-select-option :value="3">审核拒绝</a-select-option>
            <a-select-option :value="4">打款失败</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="用户ID">
          <a-input-number
            v-model="query.userId"
            :min="1"
            placeholder="请输入用户ID"
            style="width: 140px"
          />
        </a-form-item>
        <a-form-item label="时间区间">
          <a-range-picker
            v-model="query.timeRange"
            :show-time="{ format: 'HH:mm:ss' }"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :placeholder="['起始时间', '结束时间']"
            style="width: 380px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表 -->
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <!-- 状态标签: 0 橙 / 1 蓝 / 2 绿 / 3 红 / 4 灰 -->
      <span slot="status" slot-scope="text">
        <a-tag v-if="text === 0" color="orange">待审核</a-tag>
        <a-tag v-else-if="text === 1" color="blue">待确认收款</a-tag>
        <a-tag v-else-if="text === 2" color="green">打款成功</a-tag>
        <a-tag v-else-if="text === 3" color="red">审核拒绝</a-tag>
        <a-tag v-else-if="text === 4" color="grey">打款失败</a-tag>
        <span v-else>-</span>
      </span>

      <!-- 操作列: 待审核 → 通过 / 拒绝;其他状态 → 详情 -->
      <span slot="action" slot-scope="text, record">
        <template v-if="record.status === 0">
          <a-popconfirm title="确定通过该提现申请?" @confirm="handleApprove(record.id)">
            <a style="color: #1890ff">通过</a>
          </a-popconfirm>
          <a-divider type="vertical" />
          <a style="color: red" @click="handleOpenReject(record)">拒绝</a>
        </template>
        <template v-else>
          <a style="color: #1890ff" @click="handleOpenDetail(record)">详情</a>
        </template>
      </span>
    </a-table>

    <!-- 拒绝原因弹窗 -->
    <a-modal
      v-model="rejectVisible"
      title="审核拒绝"
      :confirm-loading="rejectSubmitting"
      @ok="handleSubmitReject"
      @cancel="handleCloseReject"
    >
      <a-form-model
        ref="rejectForm"
        :model="rejectForm"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="拒绝原因" required>
          <a-textarea
            v-model="rejectForm.rejectReason"
            :rows="4"
            :max-length="200"
            placeholder="请输入拒绝原因 (1-200 字)"
          />
          <div style="text-align:right; color:#999; font-size:12px;">
            {{ (rejectForm.rejectReason || '').length }} / 200
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 详情弹窗: 渲染 apply 字段 + balance/frozenSum/available 三个统计 -->
    <a-modal
      v-model="detailVisible"
      title="提现申请详情"
      :footer="null"
      :width="720"
      @cancel="handleCloseDetail"
    >
      <a-spin :spinning="detailLoading">
        <!-- 三个余额统计 -->
        <a-row :gutter="16" style="margin-bottom: 16px;">
          <a-col :span="8">
            <a-statistic
              title="账户余额"
              :value="detail.balance || 0"
              :precision="2"
              :value-style="{ color: '#1890ff' }"
            />
          </a-col>
          <a-col :span="8">
            <a-statistic
              title="冻结金额"
              :value="detail.frozenSum || 0"
              :precision="2"
              :value-style="{ color: '#fa8c16' }"
            />
          </a-col>
          <a-col :span="8">
            <a-statistic
              title="可用余额"
              :value="detail.available || 0"
              :precision="2"
              :value-style="{ color: '#52c41a' }"
            />
          </a-col>
        </a-row>

        <!-- 申请记录详情 -->
        <a-descriptions
          v-if="detail.apply"
          title="申请信息"
          bordered
          :column="2"
          size="small"
        >
          <a-descriptions-item label="申请ID">{{ detail.apply.id }}</a-descriptions-item>
          <a-descriptions-item label="用户ID">{{ detail.apply.userId }}</a-descriptions-item>
          <a-descriptions-item label="申请金额">{{ detail.apply.amount }}</a-descriptions-item>
          <a-descriptions-item label="冻结金额">{{ detail.apply.frozenAmount }}</a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag v-if="detail.apply.status === 0" color="orange">待审核</a-tag>
            <a-tag v-else-if="detail.apply.status === 1" color="blue">待确认收款</a-tag>
            <a-tag v-else-if="detail.apply.status === 2" color="green">打款成功</a-tag>
            <a-tag v-else-if="detail.apply.status === 3" color="red">审核拒绝</a-tag>
            <a-tag v-else-if="detail.apply.status === 4" color="grey">打款失败</a-tag>
            <span v-else>-</span>
          </a-descriptions-item>
          <a-descriptions-item label="商户单号">{{ detail.apply.outBizNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="微信转账单号">{{ detail.apply.transferBillNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="确认参数时间">{{ detail.apply.transferPackageCreatedAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核 Operator">{{ detail.apply.auditOperatorId || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核时间">{{ detail.apply.auditAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="打款时间">{{ detail.apply.paidAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ detail.apply.createdAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="拒绝原因" :span="2">{{ detail.apply.rejectReason || '-' }}</a-descriptions-item>
          <a-descriptions-item label="失败原因" :span="2">{{ detail.apply.failReason || '-' }}</a-descriptions-item>
        </a-descriptions>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import {
  getWithdrawList,
  getWithdrawDetail,
  approveWithdraw,
  rejectWithdraw
} from '@/api'

export default {
  data() {
    return {
      data: [],
      loading: false,
      // 顶部搜索条件
      query: {
        status: undefined,
        userId: undefined,
        // a-range-picker 已声明 valueFormat,v-model 为 [startStr, endStr]
        timeRange: []
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      // 拒绝弹窗
      rejectVisible: false,
      rejectSubmitting: false,
      rejectForm: {
        id: null,
        rejectReason: ''
      },
      // 详情弹窗
      detailVisible: false,
      detailLoading: false,
      detail: {
        apply: null,
        balance: 0,
        frozenSum: 0,
        available: 0
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '用户ID', dataIndex: 'userId', width: 100 },
        { title: '申请金额', dataIndex: 'amount', width: 120 },
        { title: '冻结金额', dataIndex: 'frozenAmount', width: 120 },
        {
          title: '状态',
          dataIndex: 'status',
          width: 100,
          scopedSlots: { customRender: 'status' }
        },
        { title: '拒绝原因', dataIndex: 'rejectReason', ellipsis: true },
        { title: '微信转账单号', dataIndex: 'transferBillNo', ellipsis: true },
        { title: '失败原因', dataIndex: 'failReason', ellipsis: true },
        { title: '创建时间', dataIndex: 'createdAt', width: 170 },
        {
          title: '操作',
          key: 'action',
          width: 140,
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 校验时间区间合法性: 仅在两端都填写时校验,起 ≤ 终,且跨度 ≤ 366 天
    // 与 financial-flow 口径保持一致
    validateTimeRange() {
      const range = this.query.timeRange || []
      const start = range[0]
      const end = range[1]

      if (!start || !end) {
        return { ok: true, startTime: start || undefined, endTime: end || undefined }
      }

      const startMoment = moment(start)
      const endMoment = moment(end)
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return { ok: false, message: '时间格式不合法' }
      }
      if (endMoment.isBefore(startMoment)) {
        return { ok: false, message: '结束时间不能早于起始时间' }
      }
      const diffDays = endMoment.diff(startMoment, 'days', true)
      if (diffDays > 366) {
        return { ok: false, message: '时间跨度不能超过 366 天' }
      }

      return {
        ok: true,
        startTime: startMoment.toISOString(),
        endTime: endMoment.toISOString()
      }
    },
    // 加载列表
    loadData() {
      const checked = this.validateTimeRange()
      if (!checked.ok) {
        this.$message.error(checked.message)
        return
      }

      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        status: this.query.status,
        userId: this.query.userId || undefined,
        startTime: checked.startTime,
        endTime: checked.endTime
      }
      getWithdrawList(params)
        .then(res => {
          // 兼容 MyBatis-Plus Page: { records, total };同时兼容 { list, total } 与数组返回
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 查询
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    // 重置
    handleReset() {
      this.query = {
        status: undefined,
        userId: undefined,
        timeRange: []
      }
      this.pagination.current = 1
      this.loadData()
    },
    // 表格分页
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    // 通过: a-popconfirm 二次确认 → approveWithdraw → 刷新
    handleApprove(id) {
      approveWithdraw(id).then(() => {
        this.$message.success('提现审核通过')
        this.loadData()
      })
    },
    // 打开拒绝弹窗
    handleOpenReject(record) {
      this.rejectForm = {
        id: record.id,
        rejectReason: ''
      }
      this.rejectVisible = true
    },
    // 关闭拒绝弹窗
    handleCloseReject() {
      this.rejectVisible = false
      this.rejectForm = { id: null, rejectReason: '' }
    },
    // 提交拒绝
    handleSubmitReject() {
      const reason = (this.rejectForm.rejectReason || '').trim()
      if (!reason) {
        this.$message.error('请输入拒绝原因')
        return
      }
      if (reason.length > 200) {
        this.$message.error('拒绝原因不能超过 200 字')
        return
      }
      this.rejectSubmitting = true
      rejectWithdraw(this.rejectForm.id, { rejectReason: reason })
        .then(() => {
          this.$message.success('已拒绝该提现申请')
          this.rejectVisible = false
          this.rejectForm = { id: null, rejectReason: '' }
          this.loadData()
        })
        .finally(() => {
          this.rejectSubmitting = false
        })
    },
    // 打开详情弹窗,调用 getWithdrawDetail 获取数据
    handleOpenDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = { apply: null, balance: 0, frozenSum: 0, available: 0 }
      getWithdrawDetail(record.id)
        .then(res => {
          const payload = (res && res.data) || {}
          this.detail = {
            apply: payload.apply || null,
            balance: payload.balance || 0,
            frozenSum: payload.frozenSum || 0,
            available: payload.available || 0
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载详情失败')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    // 关闭详情弹窗
    handleCloseDetail() {
      this.detailVisible = false
      this.detail = { apply: null, balance: 0, frozenSum: 0, available: 0 }
    }
  }
}
</script>
